import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Send, 
  FileText, 
  Users, 
  Calendar,
  DollarSign,
  ArrowRight,
  Bot,
  User,
  Loader2,
  CheckCircle,
  Globe
} from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PhoneInput from '../components/PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RequestService = () => {
  // Chat state
  const [sessionId] = useState(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [serviceCategories, setServiceCategories] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    service_category: '',
    service_subcategory: '',
    language: '',
    pages: 1,
    notarization: false,
    rush: false,
    document_description: '',
    date: '',
    time: '',
    duration_hours: 2,
    people_count: 1,
    location: '',
    notes: ''
  });
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [workOrderId, setWorkOrderId] = useState(null);

  // Pricing data
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    // Fetch pricing info
    fetch(`${API_URL}/api/public/pricing`)
      .then(res => res.json())
      .then(data => setPricing(data))
      .catch(console.error);
    
    // Fetch service categories
    fetch(`${API_URL}/api/settings/service-categories`)
      .then(res => res.json())
      .then(data => setServiceCategories(data))
      .catch(console.error);
    
    // Initial AI greeting
    setMessages([{
      role: 'assistant',
      content: "👋 Hi there! Welcome to Bylyngo Interpreting & Translation. I'm here to help you find the right language service.\n\nWhat can I help you with today?\n\n• **Certified Translation** - For official documents\n• **Interpreting Services** - For appointments, interviews, meetings\n\nJust tell me what you need, and I'll guide you through the process!"
    }]);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || chatLoading) return;
    
    const userMsg = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/public/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: userMsg })
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        
        // Check if AI provided checkout data
        if (data.checkout_data?.ready_for_checkout) {
          // Pre-fill form with AI extracted data
          setFormData(prev => ({
            ...prev,
            service_type: data.checkout_data.service_type || '',
            language: data.checkout_data.language || '',
            pages: data.checkout_data.pages || 1,
            notarization: data.checkout_data.notarization || false,
            rush: data.checkout_data.rush || false,
            people_count: data.checkout_data.people_count || 1
          }));
          setShowForm(true);
        }
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setChatLoading(false);
    }
  };

  const calculatePrice = async () => {
    if (!formData.service_type || !formData.language) return;
    
    try {
      const res = await fetch(`${API_URL}/api/public/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_type: formData.service_type,
          language: formData.language,
          pages: formData.pages,
          people_count: formData.people_count,
          hours: formData.duration_hours,
          notarization: formData.notarization,
          rush: formData.rush
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPriceBreakdown(data);
      }
    } catch (error) {
      console.error('Price calculation failed:', error);
    }
  };

  useEffect(() => {
    if (formData.service_type && formData.language) {
      calculatePrice();
    }
  }, [formData.service_type, formData.language, formData.pages, formData.notarization, formData.rush, formData.people_count, formData.duration_hours]);

  const handleSubmit = async (e, payNow = true) => {
    e.preventDefault();
    if (!priceBreakdown) return;
    
    setSubmitting(true);
    
    try {
      // Step 1: Submit the service request (creates work order)
      const res = await fetch(`${API_URL}/api/public/submit-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          total_amount: priceBreakdown.total
        })
      });
      
      if (!res.ok) {
        toast.error('Failed to submit request');
        setSubmitting(false);
        return;
      }
      
      const data = await res.json();
      setWorkOrderId(data.work_order_id);
      
      // Step 2: If paying now, redirect to Stripe checkout
      if (payNow && priceBreakdown.total > 0) {
        toast.info('Redirecting to payment...');
        
        const paymentRes = await fetch(`${API_URL}/api/payments/create-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            work_order_id: data.work_order_id,
            origin_url: window.location.origin,
            payment_type: 'full'
          })
        });
        
        if (paymentRes.ok) {
          const paymentData = await paymentRes.json();
          
          if (paymentData.payment_required && paymentData.checkout_url) {
            // Redirect to Stripe checkout
            window.location.href = paymentData.checkout_url;
            return;
          } else if (!paymentData.payment_required) {
            // Enterprise account - no payment needed
            toast.success('Request submitted! Net 30 payment terms apply.');
            setSubmitted(true);
          }
        } else {
          // Payment creation failed, but work order was created
          toast.warning('Request submitted, but payment setup failed. We will contact you.');
          setSubmitted(true);
        }
      } else {
        // Not paying now or zero total
        setSubmitted(true);
        toast.success('Request submitted successfully!');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const serviceOptions = [
    { value: 'certified_translation', label: 'Certified Translation', icon: FileText },
    { value: 'asylum_interview', label: 'Asylum Interview Interpreting', icon: Users },
    { value: 'legal_deposition', label: 'Legal/Attorney Interpreting', icon: Users },
    { value: 'medical', label: 'Medical Interpreting', icon: Users },
    { value: 'general_interpreting', label: 'General Interpreting', icon: Users }
  ];

  const languageOptions = [
    { value: 'spanish', label: 'Spanish', tier: 1 },
    { value: 'arabic', label: 'Arabic', tier: 2 },
    { value: 'russian', label: 'Russian', tier: 2 },
    { value: 'french', label: 'French', tier: 2 },
    { value: 'somali', label: 'Somali', tier: 2 },
    { value: 'hmong', label: 'Hmong', tier: 2 },
    { value: 'chinese', label: 'Chinese (Mandarin)', tier: 2 },
    { value: 'burmese', label: 'Burmese', tier: 3 },
    { value: 'rohingya', label: 'Rohingya', tier: 3 },
    { value: 'tigrinya', label: 'Tigrinya', tier: 3 },
    { value: 'karen', label: 'Karen', tier: 3 }
  ];

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-700 flex items-center justify-center p-6">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Request Submitted!</h1>
            <p className="text-muted-foreground mb-6">
              Your order number is: <strong className="text-blue-600">{workOrderId}</strong>
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              We've sent a confirmation email to <strong>{formData.email}</strong>.<br />
              Our team will review your request and contact you shortly.
            </p>
            <Button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 hover:bg-blue-700"
            >
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-700">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
            alt="Bylyngo"
            className="h-12 w-auto"
          />
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Need help?</p>
            <p className="font-medium text-blue-600">info@bylyngo.com</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Professional Language Services
          </h1>
          <p className="text-blue-100 text-lg">
            Certified translations & professional interpreting for all your needs
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* AI Chat */}
          <Card className="h-[600px] flex flex-col">
            <CardHeader className="bg-blue-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                AI Service Assistant
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-blue-600" />
                      </div>
                    )}
                    <div 
                      className={`max-w-[80%] p-3 rounded-lg ${
                        msg.role === 'user' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content.replace(/```json[\s\S]*?```/g, '')}</p>
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                    </div>
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Thinking...</p>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              
              {/* Input */}
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type your message..."
                    className="flex-1"
                  />
                  <Button 
                    onClick={sendMessage} 
                    disabled={chatLoading || !inputMessage.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Or fill out the form directly →
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Form */}
          <Card className="h-[600px] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Request a Service
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Contact Info */}
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-muted-foreground">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Full Name *</Label>
                      <Input 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <PhoneInput 
                        value={formData.phone}
                        onChange={(value) => setFormData(prev => ({...prev, phone: value}))}
                        required
                        data-testid="request-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                </div>

                {/* Service Selection */}
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-muted-foreground">Service Details</h3>
                  <div>
                    <Label>Service Type *</Label>
                    <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v, service_category: '', service_subcategory: ''})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Category Selection - show for interpreting services */}
                  {formData.service_type && formData.service_type !== 'certified_translation' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Category</Label>
                        <Select 
                          value={formData.service_category} 
                          onValueChange={(v) => setFormData({...formData, service_category: v, service_subcategory: ''})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceCategories.interpreting?.categories?.map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.service_category && 
                       serviceCategories.interpreting?.categories
                         ?.find(c => c.value === formData.service_category)?.subcategories?.length > 0 && (
                        <div>
                          <Label>Subcategory</Label>
                          <Select 
                            value={formData.service_subcategory} 
                            onValueChange={(v) => setFormData({...formData, service_subcategory: v})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select subcategory" />
                            </SelectTrigger>
                            <SelectContent>
                              {serviceCategories.interpreting?.categories
                                ?.find(c => c.value === formData.service_category)?.subcategories
                                ?.map(sub => (
                                  <SelectItem key={sub.value} value={sub.value}>
                                    {sub.label}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Category Selection - show for translation */}
                  {formData.service_type === 'certified_translation' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Document Type</Label>
                        <Select 
                          value={formData.service_category} 
                          onValueChange={(v) => setFormData({...formData, service_category: v, service_subcategory: ''})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceCategories.translation?.categories?.map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.service_category && 
                       serviceCategories.translation?.categories
                         ?.find(c => c.value === formData.service_category)?.subcategories?.length > 0 && (
                        <div>
                          <Label>Specific Type</Label>
                          <Select 
                            value={formData.service_subcategory} 
                            onValueChange={(v) => setFormData({...formData, service_subcategory: v})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {serviceCategories.translation?.categories
                                ?.find(c => c.value === formData.service_category)?.subcategories
                                ?.map(sub => (
                                  <SelectItem key={sub.value} value={sub.value}>
                                    {sub.label}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <Label>Language *</Label>
                    <Select value={formData.language} onValueChange={(v) => setFormData({...formData, language: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a language" />
                      </SelectTrigger>
                      <SelectContent>
                        {languageOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Translation-specific fields */}
                {formData.service_type === 'certified_translation' && (
                  <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
                    <div>
                      <Label>Number of Pages</Label>
                      <Input 
                        type="number"
                        min="1"
                        value={formData.pages}
                        onChange={(e) => setFormData({...formData, pages: parseInt(e.target.value) || 1})}
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id="notarization"
                          checked={formData.notarization}
                          onCheckedChange={(c) => setFormData({...formData, notarization: c})}
                        />
                        <Label htmlFor="notarization">Notarization (+$10)</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id="rush"
                          checked={formData.rush}
                          onCheckedChange={(c) => setFormData({...formData, rush: c})}
                        />
                        <Label htmlFor="rush">Rush (+$25)</Label>
                      </div>
                    </div>
                    <div>
                      <Label>Document Description</Label>
                      <Textarea 
                        value={formData.document_description}
                        onChange={(e) => setFormData({...formData, document_description: e.target.value})}
                        placeholder="Birth certificate, diploma, etc."
                      />
                    </div>
                  </div>
                )}

                {/* Interpreting-specific fields */}
                {formData.service_type && formData.service_type !== 'certified_translation' && (
                  <div className="space-y-3 p-3 bg-orange-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Preferred Date</Label>
                        <Input 
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Preferred Time</Label>
                        <Input 
                          type="time"
                          value={formData.time}
                          onChange={(e) => setFormData({...formData, time: e.target.value})}
                        />
                      </div>
                    </div>
                    {formData.service_type === 'asylum_interview' && (
                      <div>
                        <Label>Number of People Being Interviewed</Label>
                        <Select value={String(formData.people_count)} onValueChange={(v) => setFormData({...formData, people_count: parseInt(v)})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 Person</SelectItem>
                            <SelectItem value="2">2 People</SelectItem>
                            <SelectItem value="3">3 People</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {(formData.service_type === 'medical' || formData.service_type === 'general_interpreting') && (
                      <div>
                        <Label>Estimated Duration (hours)</Label>
                        <Input 
                          type="number"
                          min="2"
                          step="0.5"
                          value={formData.duration_hours}
                          onChange={(e) => setFormData({...formData, duration_hours: parseFloat(e.target.value) || 2})}
                        />
                      </div>
                    )}
                    <div>
                      <Label>Location / Address</Label>
                      <AddressAutocomplete 
                        value={formData.location}
                        onChange={(value) => setFormData({...formData, location: value})}
                        placeholder="Start typing address or 'Video Call'"
                        data-testid="service-request-location"
                      />
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                <div>
                  <Label>Additional Notes</Label>
                  <Textarea 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any special requirements..."
                  />
                </div>

                {/* Price Summary */}
                {priceBreakdown && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Price Estimate
                    </h3>
                    <div className="space-y-1 text-sm text-green-700">
                      {priceBreakdown.breakdown.map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-green-300 flex justify-between items-center">
                      <span className="font-semibold text-green-800">Total:</span>
                      <span className="text-2xl font-bold text-green-600">${priceBreakdown.total}</span>
                    </div>
                  </div>
                )}

                {/* Submit Buttons */}
                <div className="space-y-3">
                  <Button 
                    type="submit" 
                    className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
                    disabled={!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.language || submitting || !priceBreakdown}
                    data-testid="pay-now-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <DollarSign className="h-5 w-5 mr-2" />
                        Pay Now {priceBreakdown ? `- $${priceBreakdown.total}` : ''}
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={(e) => handleSubmit(e, false)}
                    className="w-full h-10"
                    disabled={!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.language || submitting}
                    data-testid="request-quote-btn"
                  >
                    Request Quote (Pay Later)
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    Secure payment powered by Stripe
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Service Cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-white/95">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">Certified Translation</h3>
              <p className="text-sm text-muted-foreground">
                Official translations for immigration, legal, and academic documents. Starting at $50/page.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white/95">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="font-semibold mb-2">Interpreting Services</h3>
              <p className="text-sm text-muted-foreground">
                Professional interpreters for asylum interviews, legal, medical, and general needs.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white/95">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">20+ Languages</h3>
              <p className="text-sm text-muted-foreground">
                Spanish, Arabic, Russian, French, Somali, Chinese, and many more languages available.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-blue-950 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
            alt="Bylyngo"
            className="h-10 w-auto mx-auto mb-4 brightness-0 invert"
          />
          <p className="text-blue-200 text-sm">
            Professional Language Services for Immigration, Legal, Medical & More
          </p>
          <p className="text-blue-300 text-sm mt-4">
            © {new Date().getFullYear()} Bylyngo Interpreting & Translation. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RequestService;
